import { useRef, useState } from "react"
import { HttpStatusCode } from "axios"
import { Button } from "primereact/button"
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog"
import { Combobox, InputDate, getDate, useFormValidation } from "medere-ui-framework"
import { BudgetDTO } from "../../dto/budget/BudgetDTO"
import { ComboDataDTO } from "../../dto/ComboDataDTO"
import { HealthEntityPlanDTO } from "../../dto/HealthEntityPlanDTO"
import { useEntityService } from "../../servicesHooks/Entity/useEntityService"
import { generateBenefitOrderFromBudget, getBillingEntities, getModalities } from "../../servicesHooks/BillHospitalization/useBillHospitalizationService"
import { parseApiError } from "../../services/apiError"
import useNotify from "../../hooks/useNotify"
import HealthPatientCoverageModal from "../Patient/HealthPatientCoverageModal"
import NoHealthPatientCoverageModal from "./NoHealthPatientCoverageModal"
import BillItemsReadOnly from "./billHospitalization/BillItemsReadOnly"
import BillSummary from "./billHospitalization/BillSummary"
import { baselineWithAutoBillingEntity, useBillingEntityName } from "./billHospitalization/billingEntity"
import NvTooltips from "../../components/novarium/NvTooltips"
import './Budget.scss'
import './billHospitalization/novariumBill.scss'

interface PreinvoiceFromBillHospitalizationViewProps {
    budget: BudgetDTO;
    hideView: () => void;
    setSelectedAction: (action: number) => void;
    /** Las órdenes se generaron: se muestran las que se crearon. */
    onGenerated: () => void;
}

/** Campos obligatorios para generar la orden. El resto del DTO puede venir en -1 o null sin ser un error. */
const REQUIRED_FIELDS: (keyof BudgetDTO)[] = ["date", "billingentity", "attentionmodality", "agreement", "agreementplan"];

const REQUIRED_MESSAGES: Partial<Record<keyof BudgetDTO, string>> = {
    date: 'Completá la fecha de orden',
    billingentity: 'Elegí el emisor',
    attentionmodality: 'Elegí la modalidad',
    agreement: 'Elegí la obra social',
    agreementplan: 'Elegí el plan',
};

/** Para generar las órdenes alcanza con la cabecera: los ítems los toma el back de la factura guardada. */
const headerOf = (budget: BudgetDTO): BudgetDTO => ({
    ...budget,
    professionalActivities: [],
    hospitalizationDays: [],
    disposables: [],
    equipments: [],
    nutritionalSupportItems: [],
    medicines: [],
    extras: [],
});

/** "Generar orden de prestación" desde una facturación sanatorial, con el design system Novarium. */
export default function PreinvoiceFromBillHospitalizationView({ budget, hideView, setSelectedAction, onGenerated }: PreinvoiceFromBillHospitalizationViewProps) {
    const { getHealthEntityComboDataDTO, getHealthEntityPlanDTO } = useEntityService();

    const notify = useNotify();
    const rootRef = useRef<HTMLDivElement>(null);
    // Mientras se genera, el botón no se puede volver a tocar: un doble click generaba las órdenes dos veces
    const [generating, setGenerating] = useState<boolean>(false);
    const [initialBudget] = useState<BudgetDTO>(() => ({
        ...budget,
        date: new Date(),
        // La duración ya no se pide en la pantalla: se manda la de la facturación, o 1 si no tiene.
        duration: budget.duration ?? 1,
        patientdesc: budget.addressee || budget.patientdesc || ''
    }));
    const [budgetToPreInvoice, setBudgetToPreInvoice] = useState<BudgetDTO>(initialBudget);
    const { errors: formErrors, handleValidation, cleanError } = useFormValidation(budgetToPreInvoice);
    const hasBillingEntity = (initialBudget.billingentity ?? -1) > 0;

    // El emisor viene con la entidad facturadora guardada en la facturación: se busca el nombre para mostrarla.
    useBillingEntityName(initialBudget.billingentity, initialBudget.billingentitydesc, (name) =>
        setBudgetToPreInvoice(prev => prev.billingentity === initialBudget.billingentity ? { ...prev, billingentitydesc: name } : prev));

    const [openNoHealthPatientCoverageModal, setOpenNoHealthPatientCoverageModal] = useState<boolean>(false);
    const [openHealthPatientCoverageModal, setOpenHealthPatientCoverageModal] = useState<boolean>(false);

    const patientName = budgetToPreInvoice.patientdesc || budgetToPreInvoice.addressee || '';
    const hasAgreement = (budgetToPreInvoice.agreement ?? -1) > 0;

    const fieldError = (field: keyof BudgetDTO) => Boolean(formErrors[field]);
    const fieldCaption = (field: keyof BudgetDTO) => formErrors[field] ? REQUIRED_MESSAGES[field] : undefined;

    function generateOrder(toGenerate: BudgetDTO, withValidation: boolean) {
        if (generating) return;
        if (withValidation) {
            const ignoredFields = (Object.keys(toGenerate) as (keyof BudgetDTO)[]).filter(field => !REQUIRED_FIELDS.includes(field));
            handleValidation(
                () => generateOrder(toGenerate, false),
                {},
                ignoredFields,
                () => setTimeout(() => rootRef.current?.querySelector('.p-invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0)
            );
            return;
        }
        setGenerating(true);
        generateBenefitOrderFromBudget(headerOf(toGenerate))
            .then(() => {
                notify.success('Órdenes generadas');
                onGenerated();
            })
            .catch((error: unknown) => {
                const apiError = parseApiError(error);
                // El paciente no tiene la cobertura de la obra social de la factura: se ofrece cargarla
                if (apiError.status === HttpStatusCode.PreconditionFailed) {
                    setOpenNoHealthPatientCoverageModal(true);
                    return;
                }
                const isBusinessError = apiError.status >= 400 && apiError.status < 500;
                notify.error(isBusinessError ? apiError.message : 'No se pudo generar la orden. Intentá de nuevo.');
            })
            .finally(() => setGenerating(false));
    }

    /** « Volver y Cancelar: si hay cambios, pregunta antes de salir. */
    function requestExit(exit: () => void) {
        if (JSON.stringify(budgetToPreInvoice) === JSON.stringify(baselineWithAutoBillingEntity(initialBudget, budgetToPreInvoice))) {
            exit();
            return;
        }
        confirmDialog({
            header: '¿Salir sin generar la orden?',
            message: 'Los cambios que hiciste se van a perder.',
            className: 'nv-scope nv-confirm',
            footer: (options) => (
                <>
                    <Button type="button" link label="Seguir editando" className="nv-push-left" onClick={options.reject} />
                    <Button type="button" className="buttonMain2" outlined label="Salir sin generar" onClick={options.accept} />
                </>
            ),
            accept: exit,
        });
    }

    return (
        <div className="nv-scope nv-page" ref={rootRef}>
            <ConfirmDialog />
            <NvTooltips />

            <header className="nv-page-head">
                <Button type="button" link icon="pi pi-angle-double-left" label="Volver" className="nv-back" onClick={() => requestExit(() => setSelectedAction(-1))} />
                <h1 className="nv-h1">Generar orden de prestación</h1>
                {patientName && <span className="nv-subtitle">{patientName}</span>}
            </header>

            <section className="nv-section" aria-labelledby="nv-order-title">
                <h2 className="nv-section-title" id="nv-order-title">Datos de la orden</h2>
                <div className="nv-form-row">
                    <InputDate
                        label="Fecha de orden"
                        required
                        className="nv-w-s"
                        value={getDate(budgetToPreInvoice.date)}
                        onChange={(e: { value?: Date | null }) => { cleanError('date'); setBudgetToPreInvoice(prev => ({ ...prev, date: e.value ?? null })); }}
                        error={fieldError('date')}
                        caption={fieldCaption('date')}
                    />
                    <Combobox
                        label="Emisor"
                        required
                        className="nv-w-m"
                        getItems={getBillingEntities}
                        // Viene la guardada y se puede cambiar; si no hay ninguna, se elige sola solo si hay una sola
                        allowsEmptyValues
                        autoselectOnOneItem={!hasBillingEntity}
                        optionLabel="name"
                        placeholder="Seleccionar"
                        value={budgetToPreInvoice.billingentitydesc}
                        setValue={(e: ComboDataDTO | null) => {
                            if (!e?.id) return;
                            cleanError('billingentity');
                            setBudgetToPreInvoice(prev => ({ ...prev, billingentity: e.id ?? -1, billingentitydesc: e.name ?? '' }));
                        }}
                        error={fieldError('billingentity')}
                        caption={fieldCaption('billingentity')}
                    />
                    <Combobox
                        label="Modalidad"
                        required
                        className="nv-w-s"
                        getItems={getModalities}
                        allowsEmptyValues={false}
                        optionLabel="name"
                        placeholder="Seleccionar"
                        value={budgetToPreInvoice.attentionmodalitydesc}
                        setValue={(e: ComboDataDTO | null) => {
                            if (!e?.id) return;
                            cleanError('attentionmodality');
                            setBudgetToPreInvoice(prev => ({ ...prev, attentionmodality: e.id ?? -1, attentionmodalitydesc: e.name ?? '' }));
                        }}
                        error={fieldError('attentionmodality')}
                        caption={fieldCaption('attentionmodality')}
                    />
                </div>
            </section>

            <section className="nv-section" aria-labelledby="nv-order-patient">
                <h2 className="nv-section-title" id="nv-order-patient">Paciente</h2>
                <div className="nv-form-row">
                    {/* Datos personales del paciente: vienen de la facturación y no se editan acá. */}
                    <div className="nv-ro nv-ro-aligned nv-w-m">
                        <span className="nv-ro-label">Paciente</span>
                        <span className="nv-ro-value nv-truncate">{patientName || '—'}</span>
                    </div>
                    <div className="nv-ro nv-ro-aligned nv-w-s">
                        <span className="nv-ro-label">DNI</span>
                        <span className="nv-ro-value">{budgetToPreInvoice.dni || '—'}</span>
                    </div>
                </div>
                <div className="nv-form-row">
                    <Combobox
                        label="Obra social"
                        required
                        className="nv-w-m"
                        getItems={getHealthEntityComboDataDTO}
                        optionLabel="name"
                        placeholder="Seleccionar"
                        value={budgetToPreInvoice.agreementdesc}
                        forceSelection={false}
                        setValue={(e: ComboDataDTO | null) => {
                            if (!e) return;
                            if (e.id) cleanError('agreement');
                            // Mientras se tipea, la obra social deja de estar elegida.
                            setBudgetToPreInvoice(prev => ({ ...prev, agreement: e.id ?? -1, agreementdesc: e.name ?? '', agreementplan: -1, agreementplandesc: '' }));
                        }}
                        error={fieldError('agreement')}
                        caption={fieldCaption('agreement')}
                    />
                    <Combobox
                        label="Plan"
                        required
                        className="nv-w-m"
                        getItems={getHealthEntityPlanDTO}
                        params={{ healthentity: budgetToPreInvoice.agreement }}
                        optionLabel="name"
                        placeholder={hasAgreement ? "Seleccionar" : "Elegí una obra social primero"}
                        disable={!hasAgreement}
                        value={budgetToPreInvoice.agreementplandesc || ""}
                        forceSelection={false}
                        setValue={(e: HealthEntityPlanDTO | null) => {
                            if (!e) return;
                            if (e.healthentityplan) cleanError('agreementplan');
                            setBudgetToPreInvoice(prev => ({ ...prev, agreementplan: e.healthentityplan ?? -1, agreementplandesc: e.name ?? '' }));
                        }}
                        reLoadItemsValue={budgetToPreInvoice.agreement}
                        error={fieldError('agreementplan')}
                        caption={fieldCaption('agreementplan')}
                    />
                </div>
            </section>

            {/* Los ítems son los de la factura guardada: para cambiarlos se edita la factura antes de prefacturar */}
            <BillItemsReadOnly budget={budgetToPreInvoice} />

            <BillSummary budget={budgetToPreInvoice} />

            <footer className="nv-page-foot">
                <hr className="nv-divider" />
                <div className="nv-actions-end">
                    <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={() => requestExit(hideView)} />
                    <Button type="button" className="buttonMain" label="Generar orden" loading={generating} disabled={generating} onClick={() => generateOrder(budgetToPreInvoice, true)} />
                </div>
            </footer>

            {/* TODO(design-system): los modales de cobertura todavía usan el estilo anterior. */}
            <NoHealthPatientCoverageModal patientName={patientName} healthEntity={budgetToPreInvoice.agreementdesc ?? ''} openNoHealthPatientCoverageModal={openNoHealthPatientCoverageModal} setOpenNoHealthPatientCoverageModal={setOpenNoHealthPatientCoverageModal} setOpenHealthPatientCoverageModal={setOpenHealthPatientCoverageModal} />
            <HealthPatientCoverageModal
                header={`Nueva cobertura para ${patientName}`}
                defaultCoverage={{
                    healthpatientcoverage: -1,
                    coveragestart: new Date(),
                    coverageend: null,
                    patient: budgetToPreInvoice.patient,
                    patientdesc: patientName,
                    healthentity: budgetToPreInvoice.agreement,
                    healthentitydesc: budgetToPreInvoice.agreementdesc,
                    healthentityplan: budgetToPreInvoice.agreementplan,
                    healthentityplandesc: budgetToPreInvoice.agreementplandesc,
                    affiliatenumber: (() => {
                        const match = budgetToPreInvoice.affiliatenumber?.match(/\d+/);
                        return match ? parseInt(match[0], 10) : null;
                    })(),
                    voluntary: false,
                    primarycoverage: false,
                }}
                openHealthPatientCoverageModal={openHealthPatientCoverageModal}
                setOpenHealthPatientCoverageModal={setOpenHealthPatientCoverageModal}
            />
        </div>
    )
}
